// Internal helpers shared across subBasinEntities.ts/zoneEntities.ts/
// outletPointEntities.ts/aquiferEntities.ts — none of these are part of
// projectEntities.ts's public API (nothing outside those 4 files, plus
// geoImport.ts, imports from here), except NO_MODEL (re-exported by the
// projectEntities.ts barrel, already public before the 2026-08 split) and
// nextId, generic enough that updateProjectFromCsv.ts also uses it
// directly (it used to hand-write its own near-identical copy, nextCode).
import { removeColumnFromSeries, type DailySeries } from "./dailySeries.ts";
import type { EvalHidProject, SubBasin } from "./types.ts";

/** No model assigned: 0 isn't a real APO_TIPO_MODELO code (those start at 1). */
export const NO_MODEL = 0;

/** `reduce`, not `Math.max(...items.map(field))` — the spread form hits JS engines' real call-argument-count limit on a large enough array; a plain accumulator has no such ceiling. */
export function nextId<T>(items: T[], field: (item: T) => number): number {
  return items.reduce((max, item) => Math.max(max, field(item)), 0) + 1;
}

export function updateSubBasins(
  project: EvalHidProject,
  codes: ReadonlySet<number>,
  update: (c: SubBasin) => SubBasin
): EvalHidProject {
  return {
    ...project,
    subBasins: project.subBasins.map((c) => (codes.has(c.subBasinCode) ? update(c) : c)),
  };
}

/** Unique name: if it already exists, adds a "_(n)" suffix (same behavior as Acuiferos.vb with duplicate names). */
export function uniqueName(name: string, existing: string[]): string {
  if (!existing.includes(name)) return name;
  let n = 2;
  while (existing.includes(`${name}_(${n})`)) n++;
  return `${name}_(${n})`;
}

interface DeleteSeriesColumnResult<T> {
  /** Updated entity list (sub-basins or aquifers) — the caller merges it back into the project. */
  entities: T[];
  /** Updated series — null if the deleted column was the series' only one. Unchanged (same reference) when the column turned out to be shared. */
  series: DailySeries | null;
  /** True if the column was left in place because another entity still references it (only this one's link was removed) — possible in v1.0 projects, which link precipitation/ETP/temperature by index and can have two sub-basins pointing at the same column (see CLAUDE.md). */
  sharedWithOthers: boolean;
}

/**
 * Permanently deletes ONE entity's own data from `series` — unlike
 * updateSubBasinColumn/updateAquiferColumn (which only unlink, index=0,
 * leaving the column itself untouched), this removes the column outright
 * when the entity is its only user, and shifts down every OTHER entity's
 * index into the same field so they keep pointing at the right column
 * after the removal. If the column is still shared with another entity, only
 * this one gets unlinked — deleting the data would silently break whoever
 * else is using it.
 */
export function deleteEntitySeriesColumn<T>(
  entities: T[],
  series: DailySeries,
  isTarget: (e: T) => boolean,
  indexOf: (e: T) => number,
  withIndex: (e: T, index: number) => T
): DeleteSeriesColumnResult<T> {
  const target = entities.find(isTarget);
  const index = target ? indexOf(target) : 0;
  if (!target || index === 0) return { entities, series, sharedWithOthers: false };

  const sharedWithOthers = entities.some((e) => !isTarget(e) && indexOf(e) === index);
  if (sharedWithOthers) {
    return {
      entities: entities.map((e) => (isTarget(e) ? withIndex(e, 0) : e)),
      series,
      sharedWithOthers: true,
    };
  }

  const newEntities = entities.map((e) => {
    if (isTarget(e)) return withIndex(e, 0);
    const i = indexOf(e);
    return i > index ? withIndex(e, i - 1) : e;
  });
  return {
    entities: newEntities,
    series: removeColumnFromSeries(series, index),
    sharedWithOthers: false,
  };
}
